import LargeRadiusTextInput from 'components/form/LargeRadiusTextInput';
import SortedTable from 'components/form/SortedTable';
import { Formik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { EpdLinks } from 'routes/EpdRoutes';
import { EPDILCDRelationModel, FindEPDILCDRelationsPagedModel } from 'services/EpdClient';
import AdminService from 'services/admin/AdminService';
import styled from 'styled-components';
import { Paginator } from 'primereact/paginator';
import { Dropdown } from 'primereact/dropdown';
import {
  AdminBox,
  AdminSearchGridForm,
  ButtonSmall,
  Container,
  EditFull,
  ErrorMessage,
  FixedLabel,
  H2,
  LabelBox,
  Message,
  MessageBox,
  ResultMessageBox,
  SearchBox,
  ValueBox,
} from 'styles/Styles.styled';
import Spinner from 'styles/spinner.styled';

const PAGE_SIZES = [10, 20, 50];
const DEFAULT_PAGE_SIZE = PAGE_SIZES[0];

const IlcdEpd = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const [pagedResult, setPagedResult] = useState<FindEPDILCDRelationsPagedModel | undefined>();
  const [underSearch, setUnderSearch] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [lastSearchValues, setLastSearchValues] = useState<{ name: string; environdecType?: string; ilcdType?: string }>({
    name: '',
  });

  const editIlcdEpdRelation = (item: EPDILCDRelationModel) => {
    item.epdVersionId && history.push(EpdLinks.ilcdEpdMappingList(item.epdId || ''));
  };

  const titles = [
    { label: t('integration.ilcdEpd.envObject'), field: 'environdecObjectName', fieldStyle: { width: 'auto' } },
    { label: t('integration.ilcdEpd.companyName'), field: 'companyName', fieldStyle: { width: 'auto' } },
    { label: t('integration.ilcdEpd.ilcdEpdVersion'), field: 'ilcdVersion', fieldStyle: { width: 'auto' } },
    { label: t('integration.ilcdEpd.published'), field: 'publishedToEcoplatform', fieldStyle: { width: 'auto' } },
  ];

  const initialValues = {
    name: '',
    environdecType: null,
    ilcdType: null,
  };

  const fetchResults = async (
    values: { name: string; environdecType?: any; ilcdType?: any },
    page: number,
    size: number
  ) => {
    setLoading(true);
    try {
      const result = await AdminService.findIlcdEpdIntegrations(
        values.name,
        values.environdecType?.value,
        values.ilcdType?.value,
        page,
        size
      );
      setPagedResult(result);
      setUnderSearch(true);
    } finally {
      setLoading(false);
    }
  };

  const onSearch = (values: any) => {
    setLastSearchValues(values);
    setPageNumber(1);
    fetchResults(values, 1, pageSize);
  };

  const foundItems = pagedResult?.items ?? [];
  const totalCount = pagedResult?.totalCount ?? 0;
  const numberOfFoundItems = totalCount;

  const paginatorTemplate = {
    layout: 'RowsPerPageDropdown CurrentPageReport PrevPageLink PageLinks NextPageLink',
    RowsPerPageDropdown: (options: any) => (
      <>
        <span style={{ marginRight: '0' }}>Show:</span>
        <Dropdown
          value={options.value}
          options={options.options.map((opt: { label: string; value: number }) => ({
            label: `${opt.label} items`,
            value: opt.value,
          }))}
          onChange={(e) =>
            options.onChange({
              originalEvent: e.originalEvent,
              value: e.value,
            })
          }
          className="paginator-dropdown"
        />
        <span style={{ marginLeft: 10 }}>Total count: {totalCount}</span>
      </>
    ),
    CurrentPageReport: <span style={{ marginLeft: 'auto' }} />,
  };

  return (
    <Container>
      <EditFull>
        <H2>{t('integration.ilcdEpd.ilcdEpdMapping')}</H2>
        <Formik initialValues={initialValues} onSubmit={onSearch}>
          <AdminBox>
            <AdminSearchGridForm>
              <LabelBox>
                <FixedLabel>{t('integration.ilcdEpd.filter')}</FixedLabel>
              </LabelBox>
              <ValueBox>
                <LargeRadiusTextInput
                  style={{ boxSizing: 'border-box', width: '100%' }}
                  name="name"
                  type="text"
                  placeholder=""
                />
              </ValueBox>

              <SearchBox>
                <ButtonSmall style={{ padding: '0.5rem 1rem', borderRadius: '3px' }} type="submit">
                  {t('integration.ilcdEpd.search')}
                </ButtonSmall>
              </SearchBox>
            </AdminSearchGridForm>
          </AdminBox>
        </Formik>

        {loading && !underSearch && (
          <div>
            <Spinner />
          </div>
        )}

        {!loading && underSearch && foundItems.length === 0 && (
          <ErrorMessage style={{ marginTop: '1rem', color: 'black' }}>{t('integration.ilcdEpd.notFound')}</ErrorMessage>
        )}

        {underSearch && foundItems.length > 0 && (
          <>
            <MessageBox>
              <ResultMessageBox>
                {numberOfFoundItems === 1 ? (
                  <Message style={{ marginLeft: 0 }}>Found {numberOfFoundItems} match</Message>
                ) : (
                  <Message>Found {numberOfFoundItems} matches</Message>
                )}
              </ResultMessageBox>
            </MessageBox>
            <TableSection>
              <TableWrapper>
                <SortedTable columns={titles} collection={foundItems} onClick={editIlcdEpdRelation} />

                {loading && <TableLoadingOverlay />}
              </TableWrapper>

              <PaginatorContainer isLoading={loading}>
                <StyledPaginator
                  first={(pageNumber - 1) * pageSize}
                  rows={pageSize}
                  totalRecords={totalCount}
                  rowsPerPageOptions={PAGE_SIZES}
                  onPageChange={(e) => {
                    const newPage = (e.page ?? 0) + 1;
                    const newSize = e.rows;
                    setPageNumber(newPage);
                    setPageSize(newSize);
                    fetchResults(lastSearchValues, newPage, newSize);
                  }}
                  template={paginatorTemplate as any}
                />
              </PaginatorContainer>
            </TableSection>
          </>
        )}
      </EditFull>
    </Container>
  );
};

const StyledPaginator = styled(Paginator)`
  &.p-paginator {
    background-color: transparent;
    padding: 0 0 0.25rem 0;
    ${(props) => props.theme.typography.contentTableBodyXs};
    border: 0;
    margin-top: 20px;
  }

  &&& .paginator-dropdown {
    padding: 0;
    align-items: center;
    border: none;
    outline: none;
    border-radius: 2px;
    box-shadow: none;
    font-weight: 600;
    font-family: 'Inter';
    & .p-inputtext {
      padding: 0;
      font-size: 12px;
      color: ${(props) => props.theme.colors.primaryBlack};
      justify-content: center;
    }
    & .p-dropdown-trigger {
      color: ${(props) => props.theme.colors.primaryBlack};
      width: 10px;
      justify-content: center;
      margin-left: 8px;
    }
  }

  &&& .paginator-dropdown.p-dropdown:not(.p-disabled).p-focus {
    outline: none !important;
    box-shadow: none;
    border: none;
  }
`;

const TableSection = styled.div`
  margin-top: -1.2rem;
`;

const TableWrapper = styled.div`
  position: relative;
`;

const TableLoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  padding: 3.1rem 4px 8px;
  background: rgba(255, 255, 255, 0.65);
  pointer-events: none;
`;

const PaginatorContainer = styled.div<{ isLoading: boolean }>`
  ${(props) =>
    props.isLoading &&
    `
      pointer-events: none;
      opacity: 0.9;
    `}
`;

export default IlcdEpd;
